import { ReactElement, useState } from 'react'
import { Switch } from 'app/components/ui/switch'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'

// Shared cell/control building blocks for the two feature-toggle table views
// (ByFeatureView / ByTargetView). Kept presentational — no store or network.

// The primary "name" cell contents: label, muted slug, and an optional
// description caption underneath.
export function ToggleLabelCell({
  label,
  slug,
  description,
}: {
  label: string
  slug: string
  description?: string
}): ReactElement {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="truncate font-medium text-strong">{label}</span>
        <span className="truncate font-mono text-[11px] text-minimal">{slug}</span>
      </div>
      {description && <p className="mt-0.5 truncate text-xs text-subtle">{description}</p>}
    </div>
  )
}

export type ToggleSwitchProps = {
  label: string
  checked: boolean
  disabled?: boolean
  disabledReason?: string
  onCheckedChange: (next: boolean) => void | Promise<void>
}

// A switch that stays disabled for the true duration of the mutation (awaits the
// handler) and shows a small spinner while saving, so rapid double-clicks can't
// fire overlapping mutations.
export function ToggleSwitch({
  label,
  checked,
  disabled,
  disabledReason,
  onCheckedChange,
}: ToggleSwitchProps): ReactElement {
  const [saving, setSaving] = useState(false)

  const handleChange = async (next: boolean) => {
    setSaving(true)
    try {
      await onCheckedChange(next)
    } finally {
      setSaving(false)
    }
  }

  const control = (
    <span className="inline-flex items-center gap-2">
      <Switch
        checked={checked}
        disabled={disabled || saving}
        onCheckedChange={handleChange}
        aria-label={`Toggle ${label}`}
      />
      {saving && (
        <span className="h-3 w-3 shrink-0 animate-spin rounded-full border border-strong border-t-(--fg-subtle)" />
      )}
    </span>
  )

  return disabled && disabledReason ? <TooltipWrapper message={disabledReason}>{control}</TooltipWrapper> : control
}
